import { useEffect, useRef } from 'react';
import classNames from 'classnames';
import css from './SignatureComponent.module.scss';
import { CTAButton } from '../ButtonBase/index';
import { useLocalization, useLocalizationExtension } from '../../context/Localization/localization';
import { useKioskState } from '../../context/Kiosk/kiosk';
import { AppointmentDispatchTypes, useAppointmentDispatch, useDisclaimerInput, useEstimateSignature } from '../../context/Appointment/appointment';
import { default as SignatureCanvas } from 'react-signature-canvas';
import { GenericClickable } from '../GenericClickable/GenericClickable';
import { GMFormCheckbox } from '../Checkbox';
import ErrorMessage from '../Form/FormHelperText/ErrorMessage/ErrorMessage';
import { GoMotoComponent } from '../../API/GoMoto.types';
import { SignatureComponentProps } from './SignatureComponent.types';
import { LocalizationType } from '../../context/Localization/localization.types';

export const SignatureComponent: GoMotoComponent<SignatureComponentProps> = ({
    setIsEmpty,
    disclaimerPage,
    processing,
    signatureRequired,
}) => {
    const Localization: LocalizationType = useLocalization();
    const { formatRequired } = useLocalizationExtension();
    const {
        dealer: {
            consentToSignElectronicallyEnabled,
            localization: {
                declineConsentToSignMessage,
                consentToSignMessage,
            },
        },
    } = useKioskState();

    const { gaveConsentToSignElectronically, saveSignature } = useDisclaimerInput();
    const appointmentDispatch = useAppointmentDispatch();

    const estimateSignature: string | undefined = useEstimateSignature();
    const signatureCanvas = useRef<SignatureCanvas>(null);

    /*
     * Due to some mobile browsers' url bars resizing the height of the window,
     * issues arise where the clearOnResize for React signature canvas is triggered
     * (default boolean is true). This adds an eventListener and clears canvas manually. clearOnUpdate
     * is now set to false to prevent height changes from clearing canvas. Changing height doesn't
     * cause a de-sync issue due to canvas stretching like changing the width does
     */

    const prevWidthRef = useRef<number>(window.innerWidth);

    useEffect(() => {
        if (estimateSignature === undefined && signatureCanvas.current) {
            signatureCanvas.current.clear();
        }
    }, [estimateSignature]);

    useEffect(() => {
        if (disclaimerPage && estimateSignature !== undefined && signatureCanvas.current) {
            signatureCanvas.current.fromDataURL(estimateSignature);
            if (setIsEmpty) {
                setIsEmpty(false);
            }
        }
    }, [disclaimerPage, estimateSignature, setIsEmpty]);

    useEffect(() => {
        function handleResize() {
            if (window.innerWidth !== prevWidthRef.current) {
                prevWidthRef.current = window.innerWidth;
                clearCanvas();
            }
            if (disclaimerPage && estimateSignature !== undefined) {
                signatureCanvas.current?.fromDataURL(estimateSignature);
            }
        }

        const clearCanvas = () => {
            if (signatureCanvas.current) {
                const canvas = signatureCanvas.current.getCanvas();
                const ratio = Math.max(window.devicePixelRatio ?? 1, 1);
                canvas.width = canvas.offsetWidth * ratio;
                canvas.height = canvas.offsetHeight * ratio;
                canvas.getContext('2d')!.scale(ratio, ratio);
                signatureCanvas.current.clear();
            }
            if (setIsEmpty) {
                setIsEmpty(true);
            }

            if (disclaimerPage) {
                appointmentDispatch({
                    type: AppointmentDispatchTypes.EstimateSignature,
                    payload: undefined,
                });
            }
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [appointmentDispatch, disclaimerPage, estimateSignature, prevWidthRef, setIsEmpty, signatureCanvas]);

    return (
        <>
            <div className={css.clear}>
                {(gaveConsentToSignElectronically || !consentToSignElectronicallyEnabled) && (
                    <GenericClickable
                        onClick={() => {
                            if (setIsEmpty) {
                                setIsEmpty(true);
                            }
                            if (disclaimerPage) {
                                appointmentDispatch({
                                    type: AppointmentDispatchTypes.EstimateSignature,
                                    payload: undefined,
                                });
                            }
                            signatureCanvas?.current?.clear();
                        }}
                    >
                        {Localization.Clear}
                    </GenericClickable>
                )}
            </div>
            {gaveConsentToSignElectronically || !consentToSignElectronicallyEnabled ? (
                <>
                    <SignatureCanvas
                        canvasProps={{ className: classNames(css.signature, { [css.error]: signatureRequired }),
                            tabIndex: 0 }}
                        ref={signatureCanvas}
                        clearOnResize={false}
                        onBegin={() => {
                            if (setIsEmpty) {
                                setIsEmpty(false);
                            }
                        }}
                        onEnd={() => {
                            const data: string | undefined = signatureCanvas.current?.toDataURL();
                            if (disclaimerPage && data) {
                                appointmentDispatch({
                                    type: AppointmentDispatchTypes.EstimateSignature,
                                    payload: data,
                                });
                            }
                        }}
                    />
                    {!disclaimerPage && saveSignature && estimateSignature !== undefined && (
                        <div className={classNames(css.applySignature)}>
                            <GenericClickable
                                onClick={() => {
                                    signatureCanvas?.current?.clear();
                                    signatureCanvas.current?.fromDataURL(estimateSignature);
                                    if (setIsEmpty) {
                                        setIsEmpty(false);
                                    }
                                }}
                                title={Localization.ClickToInsertSignature}
                            >
                                {Localization.ApplySignature}
                            </GenericClickable>
                        </div>
                    )}
                </>
            ) : (
                <div className={css.noSignatureContainer}>
                    <div className={css.noSignatureBox}>
                        <div className={css.noSignatureBoxInner}>
                            <div className={css.noSignature}>{Localization.NoSignature}</div>
                            <div className={css.seeServiceAdvisor}>{declineConsentToSignMessage ?? Localization.PleaseSeeAServiceAdvisorToComplete}</div>
                        </div>
                        <div style={{ display: 'flex',
                            flexDirection: 'row' }}
                        >
                            <div className={css.innerDisclaimer}>{consentToSignMessage ?? Localization.BySelectingAgreeAndSign}</div>
                            <div className={css.agreeButton}>
                                <CTAButton
                                    styleType={'secondary'}
                                    onClick={() => {
                                        appointmentDispatch({
                                            type: AppointmentDispatchTypes.GaveConsentToSignElectronically,
                                            payload: true,
                                        });
                                    }}
                                    disabled={processing}
                                    label={Localization.AgreeAndSign}
                                    hideIcon
                                    title={Localization.ClickToAgreeWithElectronicSignatures}
                                    ariaLabel={Localization.ClickToAgreeWithElectronicSignatures}
                                    id={Localization.ClickToAgreeWithElectronicSignatures}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <ErrorMessage error={signatureRequired}>
                {formatRequired(Localization.Signature)}
            </ErrorMessage>
            {(gaveConsentToSignElectronically || !consentToSignElectronicallyEnabled) && disclaimerPage && (
                <div className={css.saveBoxContainer}>
                    <GMFormCheckbox
                        label={Localization.SaveSignature}
                        checked={saveSignature}
                        title={Localization.ClickToSaveSignature}
                        onChange={() => {
                            appointmentDispatch({
                                type: AppointmentDispatchTypes.SaveSignature,
                                payload: !saveSignature,
                            });
                        }}
                    />
                </div>
            )}
        </>
    );
};
